// リッチプレーヤ（学習レイアウト）。動画はレターボックス（見切れ無し・余白は黒）、操作系は
// オーバーレイ（動画タップで瞬時に出/消・動画サイズは不変）。タイムラインは慣性スクロール。
import { db } from '../lib/db.js'
import { mediaURL } from '../lib/opfs.js'
import { lookupContext, dictReady } from '../lib/dict.js'
import { addWord, isSaved } from '../lib/vocab.js'
import { bgSync } from '../lib/sync.js'
import { speak, ttsAvailable } from '../lib/tts.js'
import { icon } from '../lib/icons.js'
import { t } from '../lib/i18n.js'
import { isItemLocked } from '../lib/entitlements.js'
import { showPaywall } from '../lib/paywall.js'
import { fmtDuration } from '../lib/format.js'
import { getPrefs, setPref, getItemPrefs, setItemPrefs } from '../lib/playerprefs.js'
import { loadTrackList, loadCuesByKey, pickMainKey, pickSubKey, applyOffset } from '../player/tracks.js'
import { ICONS } from '../player/icons.js'
import { openDictation } from '../player/dictation.js'

const WINDOW = 20 // タイムライン表示窓（秒）

function esc(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}
function buildWords(text) {
  const parts = String(text || '').split(/([A-Za-z][A-Za-z'-]*)/)
  const frag = document.createDocumentFragment()
  for (let i = 0; i < parts.length; i++) {
    if (!parts[i]) continue
    if (i % 2 === 1) {
      const w = document.createElement('span')
      w.className = 'word'
      w.textContent = parts[i]
      frag.appendChild(w)
    } else frag.appendChild(document.createTextNode(parts[i]))
  }
  return frag
}
// 現在時刻を mm:ss.t（0.1秒単位）で。タイムライン中央の赤棒の下に出す用。
function fmtTime1(t) {
  const x = Math.max(0, t || 0)
  const m = Math.floor(x / 60)
  const s = x - m * 60
  return `${m}:${s.toFixed(1).padStart(4, '0')}`
}
function findCue(cues, t) {
  let lo = 0, hi = cues.length - 1, ans = -1
  while (lo <= hi) {
    const m = (lo + hi) >> 1
    if (cues[m].start <= t) { ans = m; lo = m + 1 } else hi = m - 1
  }
  return ans
}

// 次トラックへ自動遷移したとき、次の項目を自動再生するためのフラグ（モジュール保持＝再描画をまたぐ）。
let autoplayOnLoad = false

// ===== 辞書ポップアップ =====
let popupEl = null
let popupWired = false
let dictReq = 0                 // スクラブ中の古い非同期結果を捨てる世代カウンタ
function hidePopup() {
  if (popupEl) popupEl.style.display = 'none'
  document.querySelectorAll('.word.cur').forEach((w) => w.classList.remove('cur')) // ハイライト解除
}
// 辞書 popup を閉じる: 語・popup内 以外を1タップ(pointerdown)で即閉じる。
// ※click 方式だとスクラブ(ドラッグ)後は click が発火せず閉じ判定が1回ズレるため pointerdown を使う。
function onDocHide(e) {
  if (!popupEl || popupEl.style.display === 'none') return
  if (popupEl.contains(e.target)) return                          // popup内(保存/TTS)は閉じない
  if (e.target.closest && e.target.closest('.word')) return       // 語は scrub 側が新規表示する
  hidePopup()
}
// ポップアップを指の少し上に置く（指で隠れないように。Mouse Dictionary のホバー相当）
function positionPopupAt(x, y) {
  if (!popupEl) return
  const p = popupEl
  let left = x - p.offsetWidth / 2
  left = Math.max(8, Math.min(left, window.innerWidth - p.offsetWidth - 8))
  let top = y - p.offsetHeight - 18
  if (top < 8) top = y + 28                            // 上に入らなければ指の下
  p.style.left = left + 'px'; p.style.top = top + 'px'
}
function fmtSense(e) {
  const m = e.match(/^(\{[^}]*\})\s*(.*)$/)
  return m ? `<div class="sense"><span class="pos">${esc(m[1])}</span>${esc(m[2])}</div>` : `<div class="sense">${esc(e)}</div>`
}
function forwardText(wordEl) {
  const cue = wordEl.closest('.sub-line') || wordEl.parentElement
  try {
    const r = document.createRange()
    r.setStartBefore(wordEl)
    r.setEndAfter(cue.lastChild || cue)
    return r.toString()
  } catch { return wordEl.textContent }
}
async function showDictFor(wordEl, anchor) {
  const surface = wordEl.textContent
  const cueText = (wordEl.closest('.sub-line')?.textContent || '').trim()
  const my = ++dictReq
  const res = await lookupContext(forwardText(wordEl))
  if (my !== dictReq) return                            // なぞり中に追い越された古い結果は捨てる
  if (!popupEl) { popupEl = document.createElement('div'); popupEl.id = 'dictPopup'; document.body.appendChild(popupEl) }
  const p = popupEl
  if (!res.found) {
    const ready = await dictReady()                     // 高速判定（重い dictCount は使わない＝なぞり中の引きをブロックしない）
    if (my !== dictReq) return                          // 待ちの間に新しい語へ移っていたら捨てる（古い「見つかりません」で上書きしない）
    p.innerHTML = `<div class="entry"><div class="head">${esc(surface)}</div><div class="sense muted">${ready ? t('player.dict.notFound') : t('player.dict.notLoaded')}</div></div>`
  } else {
    let html = ''
    for (const m of res.matches) {
      const isPhrase = m.words >= 2
      let head = esc(m.key)
      if (!isPhrase && m.key !== surface.toLowerCase()) head = `${esc(surface)} → ${esc(m.key)}`
      const tag = isPhrase ? `<span class="tag">${t('player.dict.phrase')}</span>` : ''
      // 辞書ごとにセクション分け（複数辞書が有効なときだけ辞書名の小見出しを出す）。
      const groups = m.byDict && m.byDict.length ? m.byDict : [{ name: '', entries: m.entries }]
      const multi = groups.length > 1
      let body = ''
      for (const g of groups) {
        if (multi && g.name) body += `<div class="dict-src">${esc(g.name)}</div>`
        body += g.entries.slice(0, isPhrase ? 6 : 10).map(fmtSense).join('')
      }
      html += `<div class="entry"><div class="head">${tag}${head}</div>${body}</div>`
    }
    const pick = res.matches.find((m) => m.words >= 2) || res.matches[0]
    const saved = await isSaved(pick.key)                // 既に保存済みなら「保存済み」ボタンを最初から出す
    if (my !== dictReq) return                           // 判定待ちの間に別の語へ移っていたら捨てる
    const tts = ttsAvailable() ? `<button class="mini secondary" id="dTts" aria-label="${t('common.speak')}">${icon('speaker')}</button>` : ''
    // 保存状態をボタン自体で示す（保存前=「＋単語を保存」/ 保存後・既存=「✓保存済み」で無効化）。
    const saveBtn = saved
      ? `<button class="mini ico-btn" id="dSave" disabled>${icon('check')}<span>${t('player.dict.alreadySaved')}</span></button>`
      : `<button class="mini ico-btn" id="dSave">${icon('plus')}<span>${t('player.dict.save')}</span></button>`
    html += `<div class="dict-actions">${saveBtn}${tts}</div>`
    p.innerHTML = html
    const tEl = p.querySelector('#dTts')
    if (tEl) tEl.onclick = () => speak(surface)
    const sEl = p.querySelector('#dSave')
    if (sEl && !saved) sEl.onclick = async () => {
      // ev.currentTarget は await を挟むと null になるため、ボタン参照(sEl)を直接使う。
      sEl.disabled = true
      const r = await addWord({ word: pick.key, senses: pick.entries.slice(0, 6), context: cueText })
      sEl.innerHTML = `${icon('check')}<span>${r.dup ? t('player.dict.alreadySaved') : t('player.dict.saved')}</span>`
      if (!r.dup) bgSync() // 保存した単語を裏でクラウドへ反映
    }
  }
  p.style.display = 'block'; p.style.visibility = 'hidden'
  if (anchor) {
    positionPopupAt(anchor.x, anchor.y)                 // なぞり: 指の上
  } else {
    const r = wordEl.getBoundingClientRect()            // タップ/クリック: 語の上
    let left = Math.min(r.left, window.innerWidth - p.offsetWidth - 8); if (left < 8) left = 8
    let top = r.top - p.offsetHeight - 8; if (top < 8) top = r.bottom + 8
    p.style.left = left + 'px'; p.style.top = top + 'px'
  }
  p.style.visibility = 'visible'
}

export async function renderPlayer(root, hash) {
  hidePopup()
  const d = await db()
  const item = await d.get('items', hash)
  const url = item ? await mediaURL(hash) : null
  // 戻り先：ローカル取り込み品はローカル一覧、クラウド品はクラウド一覧へ。
  const backHash = item && item.origin === 'local' ? '#/local' : '#/library'
  if (!item || !url) {
    root.innerHTML = `<div class="rp"><div class="rp-top"><button class="back" onclick="location.hash='${backHash}'">${ICONS.back}</button></div><p class="muted" style="margin:auto;padding:20px">${item ? t('player.mediaMissing') : t('player.notFound')}</p></div>`
    return
  }
  // ダウングレード（購入→無料）で無料枠超の素材はロック。購入で解放／素材は端末に残す（削除は一覧から任意）。
  if (isItemLocked(item)) {
    root.innerHTML = `<div class="rp"><div class="rp-top"><button class="back" id="lockBack">${ICONS.back}</button></div>` +
      `<div style="margin:auto;padding:24px;text-align:center;display:flex;flex-direction:column;gap:14px;align-items:center">` +
      `<div style="color:var(--accent);font-size:34px">${icon('lock')}</div>` +
      `<p class="muted">${t('player.locked')}</p>` +
      `<button class="btn" id="lockBuy">${t('player.unlock')}</button></div></div>`
    root.querySelector('#lockBack').onclick = () => { location.hash = backHash }
    root.querySelector('#lockBuy').onclick = () => showPaywall()
    return
  }

  const isVideo = item.kind === 'video'
  const prefs = await getPrefs()
  const itemPrefs = await getItemPrefs(hash)
  const trackList = await loadTrackList(hash)
  let mainKey = pickMainKey(trackList, item.metaContent, itemPrefs.mainId)
  let subKey = pickSubKey(trackList, item.metaContent, itemPrefs.subId)
  let offset = itemPrefs.offset || 0
  let mainBase = await loadCuesByKey(mainKey)
  if (!mainBase.length && itemPrefs.mainId !== '__none__') {
    // 選択トラックに cue が無い（孤立マニフェスト/古い同期で個別トラック欠落 等）。
    // subtitles.json → プールの他トラック の順で cue を持つものへフォールバック。
    // ※「メイン字幕=なし」を明示選択した場合はフォールバックしない（mainId==='__none__'）。
    const cands = [`${hash}/subtitles.json`, ...trackList.map((t) => t.key)]
    for (const k of cands) {
      if (k === mainKey) continue
      const c = await loadCuesByKey(k)
      if (c.length) { mainKey = k; mainBase = c; break }
    }
  }
  // 無音自動分割は「メイン字幕=なし(=mainBaseが空)」のときだけ帯/ループに使う。
  // 字幕が選択されていればそのcueで分割（従来どおり）。autoseg はローカル取り込み時に作成。
  const autoSegCues = await loadCuesByKey(`${hash}/autoseg.json`)
  if (!mainBase.length) mainBase = autoSegCues
  let subBase = subKey ? await loadCuesByKey(subKey) : []
  let mainCues = applyOffset(mainBase, offset)
  let subCues = applyOffset(subBase, offset)
  // 埋め込みチャプター（PC取り込み時に抽出され metaContent.chapters で届く）。メディア絶対時刻＝字幕offset非依存。
  const chapters = Array.isArray(item.metaContent?.chapters)
    ? item.metaContent.chapters.filter((c) => c && typeof c.start === 'number').sort((a, b) => a.start - b.start)
    : []
  // 再生モード（音楽プレイヤー風の循環ボタン）。stop=終端で停止 / order=一覧の次へ / shuffle=ランダム次 / repeat=この項目をくり返し。
  const MODE_ICONS = { stop: ICONS.modeStop, order: ICONS.modeOrder, shuffle: ICONS.modeShuffle, repeat: ICONS.modeRepeat }
  let playMode = prefs.playMode || 'stop'
  // ディクテーション可否: テキスト付きのメイン字幕 cue があるときだけ入口を出す（autoseg はテキスト空）。
  const hasText = mainBase.some((c) => c && String(c.text || '').trim())

  root.innerHTML = `
    <div class="rp" id="rp">
      <div class="rp-stage" id="stage">
        ${isVideo ? `<video id="media" playsinline src="${url}"></video>` : `<audio id="media" src="${url}"></audio><div class="rp-cover">${ICONS.headphone}</div>`}
        <div class="rp-subs" id="subs">
          <div class="sub-line sub-main" id="subMain"></div>
          <div class="sub-line sub-trans" id="subTrans"></div>
        </div>
      </div>
      <div class="rp-top" id="top">
        <button class="back" id="back">${ICONS.back}</button>
        <span class="rp-title" id="rpTitle">${esc(item.name || '')}</span>
        <div class="rp-top-right">
          ${chapters.length >= 2 ? `<button class="rp-ico topico" id="btnToc" title="${t('player.toc')}">${ICONS.toc}</button>` : ''}
          ${hasText ? `<button class="rp-ico topico" id="btnDict" title="${t('dct.enter')}">${ICONS.dictate}</button>` : ''}
          <button class="rp-ico topico${playMode !== 'stop' ? ' on' : ''}" id="btnMode" title="${t('player.mode.' + playMode)}">${MODE_ICONS[playMode]}</button>
          <button class="rp-gear" id="gear">${ICONS.gear}</button>
        </div>
      </div>
      <div class="rp-chrome" id="chrome">
        <div class="rp-seek" id="seekWrap">
          <div class="rp-seek-bar" id="seekbar">
            <div class="rp-seek-fill" id="skFill"></div>
            <div class="rp-seek-thumb" id="skThumb"></div>
          </div>
        </div>
        <div class="rp-timeline" id="timeline">
          <div class="tl-ruler" id="tlRuler"></div>
          <div class="tl-inner" id="tlInner"></div>
          <div class="tl-playhead"></div>
          <div class="tl-now" id="tlNow"></div>
        </div>
        <div class="rp-panel">
          <div class="rp-controls">
            <button class="rp-ico" id="btnT" title="${t('player.subSettings')}">${ICONS.subtitle}</button>
            <button class="rp-round" id="btnPrev" title="${t('player.prevSeg')}">${ICONS.prevSeg}</button>
            <button class="rp-round" id="btnReplay" title="${t('player.replaySeg')}">${ICONS.replay}</button>
            <button class="rp-round" id="btnNext" title="${t('player.nextSeg')}">${ICONS.nextSeg}</button>
            <button class="rp-ico" id="btnLoopSeg" title="${t('player.loopSeg')}">${ICONS.loopOne}</button>
            <button class="rp-spd" id="btnSpd" title="${t('player.speed')}">${prefs.speed}×</button>
            <button class="rp-round small" id="btnBack">${ICONS.back5}</button>
            <button class="rp-round play" id="btnPlay">${ICONS.play}</button>
            <button class="rp-round small" id="btnFwd">${ICONS.fwd5}</button>
            <button class="rp-ico" id="btnAB" title="${t('player.ab')}">${ICONS.ab}</button>
          </div>
        </div>
      </div>
      <div class="rp-sheet" id="sheet" hidden></div>
      <div class="rp-imm" id="rpImm">
        <div class="rp-imm-flash" id="immFlash" aria-hidden="true"></div>
        <div class="rp-imm-skip" id="immSkip" aria-hidden="true"></div>
      </div>
    </div>`

  const $ = (id) => root.querySelector('#' + id)
  const rp = $('rp'), stage = $('stage'), media = $('media')
  // mp4 等に埋め込まれた字幕(内蔵テキストトラック)はブラウザの video が勝手に焼き付け表示する。
  // アプリ側の字幕制御とは別物なので、内蔵トラックは全部 disabled にして出さない。
  const killNativeTextTracks = () => {
    const tt = media.textTracks
    if (!tt) return
    for (let i = 0; i < tt.length; i++) tt[i].mode = 'disabled'
  }
  killNativeTextTracks()
  if (media.textTracks && media.textTracks.addEventListener) {
    media.textTracks.addEventListener('addtrack', killNativeTextTracks) // 後から現れる内蔵トラックも消す
  }
  media.addEventListener('loadedmetadata', killNativeTextTracks)
  media.addEventListener('play', killNativeTextTracks) // 再生開始時に再表示される場合に備える
  const subMain = $('subMain'), subTrans = $('subTrans')
  const tlInner = $('tlInner'), tlRuler = $('tlRuler'), tlNow = $('tlNow')
  // A–B ループのピンを帯に表示（A/B の位置＋区間バンド）
  const mkPin = (l) => { const d = document.createElement('div'); d.className = 'tl-pin'; d.innerHTML = `<span class="tl-pin-l">${l}</span>`; return d }
  const pinAEl = mkPin('A'), pinBEl = mkPin('B')
  const abBandEl = document.createElement('div'); abBandEl.className = 'tl-abband'
  // メディア境界（開始0:00より前 / 終端より後）の範囲外シェード
  const edgeS = document.createElement('div'); edgeS.className = 'tl-edge tl-edge-s'
  const edgeE = document.createElement('div'); edgeE.className = 'tl-edge tl-edge-e'
  tlInner.appendChild(edgeS); tlInner.appendChild(edgeE)
  tlInner.appendChild(abBandEl); tlInner.appendChild(pinAEl); tlInner.appendChild(pinBEl)
  const playBtn = $('btnPlay')

  media.volume = prefs.volume
  media.playbackRate = prefs.speed
  rp.style.setProperty('--subPos', prefs.subPos + 'px')
  // 字幕の見た目（設定で変更）。
  rp.style.setProperty('--subSize', (prefs.subSize || 22) + 'px')
  rp.style.setProperty('--subWeight', prefs.subWeight || 700)
  rp.style.setProperty('--subColor', prefs.subColor || '#ffe14d')
  rp.style.setProperty('--subShadow', prefs.subOutline === false ? 'none' : '0 2px 6px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.95)')
  // 順番/シャッフルで次トラックへ来たときは、読み込めたら自動再生する。
  if (autoplayOnLoad) {
    autoplayOnLoad = false
    media.addEventListener('canplay', () => { media.play().catch(() => {}) }, { once: true })
  }

  let loopSeg = false, loopCueIdx = -1   // loopCueIdx=ループ対象の文(cue)を固定（desktop と同方式）
  let abState = 'idle', abA = null, abB = null
  let dictOpen = false                   // ディクテ中は本体の rAF ループ強制と自動次送りを止める（区間再生と競合しない）
  let lastMain = -2, lastSub = -2
  const boxMap = new Map()
  const seek = (t) => {
    media.currentTime = Math.max(0, Math.min(t, media.duration || t))
    if (loopSeg) loopCueIdx = findCue(mainCues, media.currentTime)  // 操作シーク先の文を新ループ対象に（閉じ込めない）
  }

  function activeAt(cues, t) {
    const i = findCue(cues, t)
    return i >= 0 && t >= cues[i].start && t < cues[i].end ? i : -1
  }
  function renderLine(el, cue) {
    el.replaceChildren()
    if (cue) { el.appendChild(buildWords(cue.text || '')); el.style.display = '' }
    else el.style.display = 'none'
  }

  function drawTimeline(t) {
    const winStart = t - WINDOW / 2
    const winEnd = winStart + WINDOW
    let lo = 0, hi = mainCues.length
    while (lo < hi) { const m = (lo + hi) >> 1; if (mainCues[m].start < winStart) lo = m + 1; else hi = m }
    const seen = new Set()
    const ai = activeAt(mainCues, t)
    for (let i = Math.max(0, lo - 1); i < mainCues.length; i++) {
      const c = mainCues[i]
      if (c.start >= winEnd) break
      if (c.end <= winStart) continue
      seen.add(i)
      let el = boxMap.get(i)
      if (!el) { el = document.createElement('div'); el.className = 'tl-box'; tlInner.appendChild(el); boxMap.set(i, el) }
      el.style.left = ((c.start - winStart) / WINDOW * 100) + '%'
      el.style.width = `calc(${Math.max(0.8, (c.end - c.start) / WINDOW * 100)}% - 3px)`  // -3px で隣の箱との間に常にすき間
      el.classList.toggle('active', i === ai)
    }
    for (const [i, el] of boxMap) if (!seen.has(i)) { el.remove(); boxMap.delete(i) }
    let ticks = ''
    const first = Math.ceil(winStart / 5) * 5
    for (let tt = first; tt < winEnd; tt += 5) {
      if (tt < 0) continue
      ticks += `<span class="tl-tick" style="left:${((tt - winStart) / WINDOW * 100)}%">${fmtDuration(tt)}</span>`
    }
    tlRuler.innerHTML = ticks
    // A–B ピン＆区間バンドの位置（ウィンドウ内のみ表示）
    const pinPos = (el, val) => {
      if (val == null) { el.style.display = 'none'; return }
      const p = (val - winStart) / WINDOW * 100
      el.style.display = (p < 0 || p > 100) ? 'none' : ''
      el.style.left = p + '%'
    }
    // メディア境界の範囲外シェード（左＝0:00より前 / 右＝終端より後）
    const durTL = media.duration || 0
    const sx = (0 - winStart) / WINDOW * 100               // 開始(0)のウィンドウ内位置
    if (sx > 0) { edgeS.style.display = ''; edgeS.style.width = Math.min(100, sx) + '%' } else edgeS.style.display = 'none'
    const ex = durTL ? (durTL - winStart) / WINDOW * 100 : 999 // 終端のウィンドウ内位置
    if (durTL && ex < 100) { edgeE.style.display = ''; edgeE.style.left = Math.max(0, ex) + '%' } else edgeE.style.display = 'none'
    pinPos(pinAEl, abA); pinPos(pinBEl, abB)
    if (abA != null && abB != null) {
      const lo = Math.max(0, (abA - winStart) / WINDOW * 100)
      const hi = Math.min(100, (abB - winStart) / WINDOW * 100)
      if (hi > lo) { abBandEl.style.display = ''; abBandEl.style.left = lo + '%'; abBandEl.style.width = (hi - lo) + '%' }
      else abBandEl.style.display = 'none'
    } else abBandEl.style.display = 'none'
  }

  function update() {
    const t = media.currentTime || 0
    const dur = media.duration || 0
    tlNow.textContent = fmtTime1(t) // 帯の赤棒の下・右に現在時間(0.1秒)
    if (!skDragging) { const f = dur ? (t / dur) * 100 : 0; skFill.style.width = f + '%'; skThumb.style.left = f + '%' }
    const mi = activeAt(mainCues, t)
    if (mi !== lastMain) { renderLine(subMain, mi >= 0 ? mainCues[mi] : null); lastMain = mi }
    if (subCues.length) {
      const si = activeAt(subCues, t)
      if (si !== lastSub) { renderLine(subTrans, si >= 0 ? subCues[si] : null); lastSub = si }
    } else if (lastSub !== -2) { renderLine(subTrans, null); lastSub = -2 }
    drawTimeline(t)
    playBtn.innerHTML = media.paused ? ICONS.play : ICONS.pause
    // 目次シートを開いている間は現在チャプターを追従ハイライト＋見える位置へ。
    if (chapters.length && !sheet.hidden && sheetMode === 'toc') {
      const ci = findCue(chapters, t)
      if (ci !== lastTocIdx) {
        const list = sheet.querySelector('#tocList')
        if (list) {
          const prev = list.querySelector('.toc-item.active'); if (prev) prev.classList.remove('active')
          const cur = ci >= 0 ? list.querySelector(`.toc-item[data-i="${ci}"]`) : null
          if (cur) { cur.classList.add('active'); cur.scrollIntoView({ block: 'nearest' }) }
        }
        lastTocIdx = ci
      }
    }
    if (!dragging && momRaf === 0 && !skDragging && !media.paused && !dictOpen) {  // ディクテ中は本体ループを黙らせる（区間再生と綱引きしない。loopSeg/abState の状態は保持）
      if (abState === 'AB' && abA != null && abB != null) {        // A–B優先: 区間外(B以降/A手前)に出たら無条件にAへ＝常に区間内だけ再生
        if (t >= abB || t < abA - 0.05) media.currentTime = abA + 0.001
      } else if (loopSeg && mainCues.length) {                     // 今の文ループ: 対象cueを固定で見て、endをまたいで次cueに入っても取りこぼさない
        if (loopCueIdx < 0) loopCueIdx = findCue(mainCues, t)
        const c = mainCues[loopCueIdx]
        if (c && (t >= c.end || t < c.start - 0.05)) media.currentTime = c.start + 0.001
      }
    }
  }
  function tick() { if (!rp.isConnected) return; update(); requestAnimationFrame(tick) }

  // ===== スクラブ辞書: 触れた瞬間に発火＋なぞると「指の下の語」に追従（Mouse Dictionary 風） =====
  let scrubbing = false, scrubWord = null
  const wordAtPoint = (x, y) => { const el = document.elementFromPoint(x, y); return el ? el.closest('.word') : null }
  // 今指がある語だけを薄くハイライト（前の語のハイライトは外す）。
  const setCur = (el) => {
    document.querySelectorAll('.word.cur').forEach((w) => { if (w !== el) w.classList.remove('cur') })
    if (el) el.classList.add('cur')
  }
  stage.addEventListener('pointerdown', (e) => {
    const w = wordAtPoint(e.clientX, e.clientY)
    if (!w) return                                       // 語以外は通常処理（onDocHide が閉じる）
    e.preventDefault()                                   // 文字選択・スクロール抑止
    // 辞書表示中に字幕を触ったら、新規表示せず今の辞書を閉じる（字幕の上タップでも閉じる）
    if (popupEl && popupEl.style.display !== 'none') { hidePopup(); return }
    scrubbing = true; scrubWord = w
    setCur(w)
    if (popupEl) popupEl.classList.remove('live')        // なぞり中は当たり判定オフ（既定none）。初回の生成タイミングにも依存しない
    try { stage.setPointerCapture(e.pointerId) } catch {}
    showDictFor(w, { x: e.clientX, y: e.clientY })
  })
  stage.addEventListener('pointermove', (e) => {
    if (!scrubbing) return
    e.preventDefault()
    const w = wordAtPoint(e.clientX, e.clientY)
    if (w && w !== scrubWord) { scrubWord = w; setCur(w); showDictFor(w, { x: e.clientX, y: e.clientY }) }
    else if (w) positionPopupAt(e.clientX, e.clientY)    // 同じ語の上は popup を指に追従
  })
  const endScrub = (e) => {
    if (!scrubbing) return
    scrubbing = false; scrubWord = null                  // ハイライトは表示中の語に残す（閉じると hidePopup が解除）
    if (popupEl) popupEl.classList.add('live')            // 指を離したら当たり判定オン（保存/TTSを押せる）
    try { stage.releasePointerCapture(e.pointerId) } catch {}
  }
  stage.addEventListener('pointerup', endScrub)
  stage.addEventListener('pointercancel', endScrub)

  // ===== 動画(非語)領域のジェスチャ =====
  //  ダブルタップ: 左半分=秒戻し / 右半分=秒送り（設定秒数）
  //  左スワイプ1: 今のセグメント頭 / 左スワイプ2連: 一つ前のセグメント頭 / 右スワイプ: 次のセグメント頭
  let gx = 0, gy = 0, gt = 0, gOnMedia = false
  let lastTapT = 0, lastLeftT = 0, leftBaseIdx = -1, singleTapTimer = 0, prevSwipeUntil = 0
  const SWIPE_MIN = 45, TAP_MOVE = 14, TAP_MS = 300, DTAP_MS = 320, DSWIPE_MS = 450
  const segGoto = (j) => { if (mainCues[j]) { seek(mainCues[j].start); media.play() } }
  // ダブルタップの秒送り/戻しヒント（« −3 / +3 »。連続で累積表示）
  let accSec = 0, skipDir = 0, skipUntil = 0
  const CONT_TAP_MS = 450 // 連続スキップ: 直前のスキップからこの時間内のタップは継続発火（同じ側）して累積
  let hintEl = null, hintTimer = 0
  const showSeekHint = (dir, sec) => {
    if (!hintEl) { hintEl = document.createElement('div'); hintEl.className = 'rp-seekhint'; $('chrome').appendChild(hintEl) }
    hintEl.textContent = dir < 0 ? `« −${sec}` : `+${sec} »`
    hintEl.classList.toggle('left', dir < 0)
    hintEl.classList.add('show')
    clearTimeout(hintTimer)
    hintTimer = setTimeout(() => { if (hintEl) hintEl.classList.remove('show') }, 800)
  }
  // 秒送り/戻し。継続中（同じ側＋0.2秒以内）なら累積（-3→-6→-9…）。没入中は円形バッジ、通常は秒数ヒント。
  const doSkip = (dir) => {
    const t = performance.now()
    if (dir === skipDir && t < skipUntil) accSec += prefs.skipSeconds
    else accSec = prefs.skipSeconds
    skipDir = dir; skipUntil = t + CONT_TAP_MS
    seek((media.currentTime || 0) + dir * prefs.skipSeconds)
    if (immersive) immSkipFlash(dir, accSec); else showSeekHint(dir, accSec)
  }
  stage.addEventListener('pointerdown', (e) => {
    gOnMedia = !wordAtPoint(e.clientX, e.clientY)   // 語の上はスクラブ辞書が処理。ここは非語のみ
    if (gOnMedia) { gx = e.clientX; gy = e.clientY; gt = performance.now() }
  })
  stage.addEventListener('pointercancel', () => { gOnMedia = false })
  stage.addEventListener('pointerup', (e) => {
    if (!gOnMedia) return
    gOnMedia = false
    const dx = e.clientX - gx, dy = e.clientY - gy, dt = performance.now() - gt
    const adx = Math.abs(dx), ady = Math.abs(dy), now = performance.now()
    if (adx >= SWIPE_MIN && adx > ady) {
      if (dx > 0) {                                  // 右スワイプ: 次セグメント頭
        lastLeftT = 0; leftBaseIdx = -1; prevSwipeUntil = 0
        const i = findCue(mainCues, media.currentTime || 0)
        segGoto((i < 0 ? -1 : i) + 1)
      } else if (now < prevSwipeUntil) {             // 2連直後の継続: 単発左スワイプでさらに前セグへ（ダブルタップ継続と同じ）
        const i = findCue(mainCues, media.currentTime || 0)
        if (i > 0) { segGoto(i - 1); prevSwipeUntil = now + DSWIPE_MS } else prevSwipeUntil = 0
        lastLeftT = 0; leftBaseIdx = -1
      } else if (now - lastLeftT < DSWIPE_MS && leftBaseIdx > 0) {  // 左スワイプ2連: 前セグメント頭
        segGoto(leftBaseIdx - 1); prevSwipeUntil = now + DSWIPE_MS  // 以降この時間内はワンスワイプで継続して前へ
        lastLeftT = 0; leftBaseIdx = -1
      } else {                                       // 左スワイプ1: 今のセグメント頭
        const i = findCue(mainCues, media.currentTime || 0)
        if (i >= 0) { leftBaseIdx = i; segGoto(i) } else leftBaseIdx = -1
        lastLeftT = now
      }
    } else if (adx < TAP_MOVE && ady < TAP_MOVE && dt < TAP_MS) {
      const side = e.clientX < window.innerWidth / 2 ? -1 : 1
      // 中央の残像マーク(86px円)とほぼ同じ大きさの当たり判定（没入の再生/一時停止用）
      const centerHit = immersive && Math.hypot(e.clientX - window.innerWidth / 2, e.clientY - window.innerHeight / 2) < 46
      const cont = !centerHit && side === skipDir && now < skipUntil  // 0.2秒以内＝継続スキップ（ワンタップで-6,-9..）
      if (cont || (!centerHit && now - lastTapT < DTAP_MS)) {         // 継続タップ or 最初のダブルタップ
        if (singleTapTimer) { clearTimeout(singleTapTimer); singleTapTimer = 0 } // 没入の単タップ動作は取り消し
        doSkip(side)
        lastTapT = 0
      } else {
        lastTapT = now
        // 没入中、中央の残像マーク円内のシングルタップで再生/一時停止＋残像（ダブルタップ/継続なら上で処理）。
        if (immersive && centerHit) {
          if (singleTapTimer) clearTimeout(singleTapTimer)
          singleTapTimer = setTimeout(() => { singleTapTimer = 0; immTogglePlay() }, DTAP_MS)
        }
      }
    }
  })

  // 辞書 popup を閉じる: 語・popup以外を1タップで即閉じる（pointerdown 即時）。
  // 操作盤・字幕は常に表示（タップでの表示/非表示トグルは廃止）。
  if (!popupWired) { document.addEventListener('pointerdown', onDocHide, { capture: true }); popupWired = true }

  // トランスポート
  $('back').onclick = () => { saveState(); location.hash = backHash }
  $('gear').onclick = () => { saveState(); location.hash = '#/settings' }
  playBtn.onclick = () => { if (media.paused) media.play(); else media.pause() }
  $('btnBack').onclick = () => seek((media.currentTime || 0) - prefs.skipSeconds)
  $('btnFwd').onclick = () => seek((media.currentTime || 0) + prefs.skipSeconds)
  $('btnReplay').onclick = () => { const i = findCue(mainCues, media.currentTime || 0); if (i >= 0) { seek(mainCues[i].start); media.play() } }
  $('btnPrev').onclick = () => { const i = findCue(mainCues, (media.currentTime || 0) - 0.4); const j = Math.max(0, (i < 0 ? 0 : i) - 1); if (mainCues[j]) { seek(mainCues[j].start); media.play() } }
  $('btnNext').onclick = () => { const i = findCue(mainCues, media.currentTime || 0); const j = (i < 0 ? -1 : i) + 1; if (mainCues[j]) { seek(mainCues[j].start); media.play() } }
  const segBtn = $('btnLoopSeg')
  segBtn.onclick = () => {
    loopSeg = !loopSeg
    loopCueIdx = loopSeg ? findCue(mainCues, media.currentTime || 0) : -1  // ON時に今いる文を対象として固定
    segBtn.classList.toggle('on', loopSeg)
  }
  // 再生モード循環ボタン（stop→order→shuffle→repeat→stop）。状態は端末に保存。
  const modeBtn = $('btnMode')
  const MODE_ORDER = ['stop', 'order', 'shuffle', 'repeat']
  modeBtn.onclick = async () => {
    playMode = MODE_ORDER[(MODE_ORDER.indexOf(playMode) + 1) % MODE_ORDER.length]
    modeBtn.innerHTML = MODE_ICONS[playMode]
    modeBtn.title = t('player.mode.' + playMode)
    modeBtn.classList.toggle('on', playMode !== 'stop')
    await setPref('playMode', playMode)
  }
  // 「次の項目」のキュー＝この項目を開いた一覧（同じ origin＋同じフォルダ）の、ライブラリと同じ名前順。
  const siblingHashes = async () => {
    const all = await d.getAll('items')
    return all
      .filter((m) => (m.origin || 'gdrive') === (item.origin || 'gdrive') && (m.folderId || null) === (item.folderId || null))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
      .map((m) => m.hash)
  }
  const goToTrack = (h) => { autoplayOnLoad = true; saveState(); location.hash = `#/play/${encodeURIComponent(h)}` }
  media.addEventListener('ended', async () => {
    if (dictOpen) return  // ディクテ中は最終文の末尾到達で勝手に次トラックへ飛ばさない
    if (playMode === 'repeat') { seek(0); media.play(); return }
    if (playMode === 'stop') return
    const sibs = await siblingHashes()
    if (sibs.length <= 1) return
    let next = null
    if (playMode === 'order') {
      const i = sibs.indexOf(hash)
      next = i >= 0 ? sibs[i + 1] || null : null  // 一覧の最後なら停止（ループしない）
    } else { // shuffle: 自分以外からランダム
      const others = sibs.filter((h) => h !== hash)
      next = others[Math.floor(Math.random() * others.length)] || null
    }
    if (next) goToTrack(next)
  })
  const abBtn = $('btnAB')
  abBtn.onclick = () => {
    const t = media.currentTime || 0
    if (abState === 'idle') { abA = t; abState = 'A'; abBtn.classList.add('half'); abBtn.classList.remove('on') }
    else if (abState === 'A') { if (t > abA) { abB = t; abState = 'AB'; abBtn.classList.remove('half'); abBtn.classList.add('on'); seek(abA); media.play() } }
    else { abState = 'idle'; abA = abB = null; abBtn.classList.remove('half', 'on') }
  }
  // 再生速度ピッカー（スロット/ドラム式: 縦スクロールで中央に来た値を選択）
  const spdBtn = $('btnSpd')
  const SPEEDS = []; for (let s10 = 5; s10 <= 30; s10++) SPEEDS.push(s10 / 10)
  const SPD_ITEMH = 34
  let spdMenu = null, spdReel = null, spdItems = null, spdOut = null, spdSettle = 0, spdSel = -1
  const closeSpdMenu = () => {
    if (spdMenu) spdMenu.classList.remove('show')
    if (spdOut) { document.removeEventListener('pointerdown', spdOut, true); spdOut = null }
  }
  const spdCenterIdx = () => Math.max(0, Math.min(SPEEDS.length - 1, Math.round(spdReel.scrollTop / SPD_ITEMH)))
  const buildSpdMenu = () => {
    spdMenu = document.createElement('div'); spdMenu.className = 'rp-spdmenu spd-wheel'
    spdMenu.innerHTML = `<div class="spd-band"></div><div class="spd-reel"><div class="spd-pad"></div>${SPEEDS.map((s) => `<div class="spd-item">${s}×</div>`).join('')}<div class="spd-pad"></div></div>`
    rp.appendChild(spdMenu)
    spdReel = spdMenu.querySelector('.spd-reel')
    spdItems = [...spdMenu.querySelectorAll('.spd-item')]
    spdReel.addEventListener('scroll', () => {
      const i = spdCenterIdx()
      if (i !== spdSel) {                                    // 変わった時だけDOM更新＝滑らか
        if (spdItems[spdSel]) spdItems[spdSel].classList.remove('sel')
        spdItems[i].classList.add('sel'); spdSel = i
        spdBtn.textContent = SPEEDS[i] + '×'                 // 表示だけ即更新（軽い）
      }
      clearTimeout(spdSettle)                                // 速度の適用/保存は「止まったら」＝回転中はカクつかない
      spdSettle = setTimeout(async () => { const s = SPEEDS[spdCenterIdx()]; prefs.speed = s; media.playbackRate = s; await setPref('speed', s) }, 130)
    })
  }
  const openSpdMenu = () => {
    if (!spdMenu) buildSpdMenu()
    spdMenu.classList.add('show')
    const r = spdBtn.getBoundingClientRect()
    spdMenu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - spdMenu.offsetWidth - 8)) + 'px'
    spdMenu.style.bottom = (window.innerHeight - r.top + 6) + 'px'
    let cur = SPEEDS.findIndex((s) => Math.abs(s - prefs.speed) < 0.001); if (cur < 0) cur = SPEEDS.indexOf(1)
    spdReel.scrollTop = cur * SPD_ITEMH
    spdItems.forEach((el, j) => el.classList.toggle('sel', j === cur)); spdSel = cur
    spdOut = (ev) => { if (!spdMenu.contains(ev.target) && ev.target !== spdBtn && !spdBtn.contains(ev.target)) closeSpdMenu() }
    document.addEventListener('pointerdown', spdOut, true)
  }
  spdBtn.onclick = () => { (spdMenu && spdMenu.classList.contains('show')) ? closeSpdMenu() : openSpdMenu() }

  // ===== 下部: 動画全体のシークバー＋スクラブ時のサムネ/時刻プレビュー =====
  const seekWrap = $('seekWrap'), seekbar = $('seekbar'), skFill = $('skFill'), skThumb = $('skThumb')
  let skDragging = false, skWasPlaying = false, drawPending = false
  const prev = document.createElement('div'); prev.className = 'rp-seek-prev'
  const prevCanvas = document.createElement('canvas'); prevCanvas.width = 264; prevCanvas.height = 148
  const prevTime = document.createElement('div'); prevTime.className = 't'
  if (isVideo) prev.appendChild(prevCanvas)
  prev.appendChild(prevTime)
  seekWrap.appendChild(prev)
  const pctx = isVideo ? prevCanvas.getContext('2d') : null
  if (isVideo) media.addEventListener('seeked', () => {
    if (!drawPending || !pctx) return
    try { pctx.drawImage(media, 0, 0, prevCanvas.width, prevCanvas.height) } catch {}
    drawPending = false
  })
  const skFrac = (clientX) => { const r = seekbar.getBoundingClientRect(); return Math.max(0, Math.min(1, (clientX - r.left) / (r.width || 1))) }
  const skRender = (f) => { skFill.style.width = (f * 100) + '%'; skThumb.style.left = (f * 100) + '%' }
  const skScrub = (clientX) => {
    const dur = media.duration || 0; if (!dur) return
    const f = skFrac(clientX); skRender(f)
    const t = f * dur
    prevTime.textContent = fmtDuration(t)
    prev.style.left = (clientX - seekWrap.getBoundingClientRect().left) + 'px'
    prev.classList.add('show')
    if (isVideo) drawPending = true
    media.currentTime = t                                // フレーム取得＆位置反映のためシーク（動画はseekedでサムネ描画）
  }
  seekWrap.addEventListener('pointerdown', (e) => {
    if (!(media.duration > 0)) return
    skDragging = true; skWasPlaying = !media.paused; media.pause()
    try { seekWrap.setPointerCapture(e.pointerId) } catch {}
    skScrub(e.clientX)
  })
  seekWrap.addEventListener('pointermove', (e) => { if (skDragging) skScrub(e.clientX) })
  const skEnd = (e) => {
    if (!skDragging) return
    skDragging = false
    const dur = media.duration || 0
    media.currentTime = skFrac(e.clientX) * dur
    prev.classList.remove('show')
    try { seekWrap.releasePointerCapture(e.pointerId) } catch {}
    if (skWasPlaying) media.play()
  }
  seekWrap.addEventListener('pointerup', skEnd)
  seekWrap.addEventListener('pointercancel', skEnd)

  // ===== タイムライン: 慣性スクラブ（タイヤ転がし） =====
  const tl = $('timeline')
  let dragging = false, wasPlaying = false
  let lastX = 0, lastTs = 0, vel = 0 // vel: メディア秒 / ms
  let momRaf = 0
  const tpp = () => WINDOW / (tl.clientWidth || 1)
  const stopMom = () => { if (momRaf) { cancelAnimationFrame(momRaf); momRaf = 0 } }
  tl.addEventListener('pointerdown', (e) => {
    stopMom(); dragging = true; wasPlaying = !media.paused; media.pause()
    lastX = e.clientX; lastTs = performance.now(); vel = 0
    tl.setPointerCapture(e.pointerId)
  })
  tl.addEventListener('pointermove', (e) => {
    if (!dragging) return
    const now = performance.now()
    const dt = -(e.clientX - lastX) * tpp() // 右へドラッグ=巻き戻し
    seek((media.currentTime || 0) + dt)
    const dms = now - lastTs
    if (dms > 0) vel = dt / dms
    lastX = e.clientX; lastTs = now
  })
  const release = () => {
    if (!dragging) return
    dragging = false
    let last = performance.now()
    const step = () => {
      const now = performance.now(); const dms = now - last; last = now
      let nt = (media.currentTime || 0) + vel * dms
      if (nt <= 0) { nt = 0; vel = 0 }
      if (media.duration && nt >= media.duration) { nt = media.duration; vel = 0 }
      media.currentTime = nt
      vel *= Math.pow(0.93, dms / 16) // フレームレート非依存の減衰
      if (Math.abs(vel) * 1000 > 0.04) momRaf = requestAnimationFrame(step)
      else { momRaf = 0; if (loopSeg) loopCueIdx = findCue(mainCues, media.currentTime || 0); if (wasPlaying) media.play() }
    }
    if (Math.abs(vel) * 1000 > 0.04) momRaf = requestAnimationFrame(step)
    else if (wasPlaying) media.play()
  }
  tl.addEventListener('pointerup', release)
  tl.addEventListener('pointercancel', release)

  // ===== T: 字幕設定シート =====
  let sheetMode = '', lastTocIdx = -2   // sheet は字幕設定('subs')と目次('toc')で共用
  const sheet = $('sheet')
  const opts = (selKey) => trackList.map((t) => `<option value="${t.id}" ${t.key === selKey ? 'selected' : ''}>${esc(t.label)}${t.lang ? ' (' + esc(t.lang) + ')' : ''}</option>`).join('')
  function renderSheet() {
    sheet.innerHTML = `
      <div class="sheet-row"><label>${t('player.sheet.main')}</label><select id="selMain"><option value="__none__" ${mainKey ? '' : 'selected'}>${t('player.sheet.none')}</option>${opts(mainKey)}</select></div>
      <div class="sheet-row"><label>${t('player.sheet.sub')}</label><select id="selSub"><option value="__none__" ${subKey ? '' : 'selected'}>${t('player.sheet.none')}</option>${opts(subKey)}</select></div>
      <div class="sheet-row"><label>${t('player.sheet.pos')}</label><div class="stepper"><button id="posDown">▼</button><span id="posVal">${prefs.subPos}px</span><button id="posUp">▲</button></div></div>
      <div class="sheet-row"><label>${t('player.sheet.timing')}</label><div class="stepper"><button id="offMinus">−0.1s</button><span id="offVal">${offset.toFixed(1)}s</span><button id="offPlus">＋0.1s</button></div></div>
      <button class="btn secondary" id="sheetClose">${t('common.close')}</button>`
    $('selMain').onchange = async (e) => {
      if (e.target.value === '__none__') {
        // 「なし」→ 無音自動分割があればそれで帯/ループ（テキストは出ない）。無ければ空。
        mainKey = null; mainBase = autoSegCues
        mainCues = applyOffset(mainBase, offset)
        renderLine(subMain, null); lastMain = -2
      } else {
        const tr = trackList.find((x) => x.id === e.target.value)
        mainKey = tr ? tr.key : mainKey; mainBase = await loadCuesByKey(mainKey)
        mainCues = applyOffset(mainBase, offset); lastMain = -2
      }
      for (const [, el] of boxMap) el.remove(); boxMap.clear()
      await setItemPrefs(hash, { mainId: e.target.value })
    }
    $('selSub').onchange = async (e) => {
      if (e.target.value === '__none__') { subKey = null; subBase = []; subCues = []; renderLine(subTrans, null); lastSub = -2 }
      else { const t = trackList.find((x) => x.id === e.target.value); subKey = t ? t.key : null; subBase = await loadCuesByKey(subKey); subCues = applyOffset(subBase, offset); lastSub = -2 }
      await setItemPrefs(hash, { subId: e.target.value })
    }
    $('posUp').onclick = async () => { prefs.subPos = Math.min(800, prefs.subPos + 10); rp.style.setProperty('--subPos', prefs.subPos + 'px'); $('posVal').textContent = prefs.subPos + 'px'; await setPref('subPos', prefs.subPos) }
    $('posDown').onclick = async () => { prefs.subPos = Math.max(0, prefs.subPos - 10); rp.style.setProperty('--subPos', prefs.subPos + 'px'); $('posVal').textContent = prefs.subPos + 'px'; await setPref('subPos', prefs.subPos) }
    const reoffset = async (dd) => {
      offset = Math.round((offset + dd) * 10) / 10
      mainCues = applyOffset(mainBase, offset); subCues = applyOffset(subBase, offset)
      lastMain = lastSub = -2; for (const [, el] of boxMap) el.remove(); boxMap.clear()
      $('offVal').textContent = offset.toFixed(1) + 's'; await setItemPrefs(hash, { offset })
    }
    $('offMinus').onclick = () => reoffset(-0.1)
    $('offPlus').onclick = () => reoffset(0.1)
    $('sheetClose').onclick = () => { sheet.hidden = true }
  }
  $('btnT').onclick = () => {
    if (!sheet.hidden && sheetMode === 'subs') { sheet.hidden = true; sheetMode = '' }
    else { renderSheet(); sheetMode = 'subs'; sheet.hidden = false }
  }

  // ===== 目次（チャプター）シート =====
  function renderTocSheet() {
    const cur = findCue(chapters, media.currentTime || 0)
    sheet.innerHTML =
      `<div class="sheet-head">${t('player.toc')}</div>` +
      `<div class="toc-list" id="tocList">` +
      chapters.map((c, i) =>
        `<button class="toc-item${i === cur ? ' active' : ''}" data-i="${i}">` +
        `<span class="toc-t">${esc(c.title || t('player.tocItem', { n: i + 1 }))}</span>` +
        `<span class="toc-time">${fmtDuration(c.start)}</span></button>`
      ).join('') +
      `</div><button class="btn secondary" id="sheetClose">${t('common.close')}</button>`
    lastTocIdx = cur
    $('tocList').onclick = (e) => {
      const b = e.target.closest('.toc-item[data-i]'); if (!b) return
      const c = chapters[Number(b.dataset.i)]
      if (c) { seek(c.start + 0.001); media.play() }
      sheet.hidden = true; sheetMode = ''
    }
    $('sheetClose').onclick = () => { sheet.hidden = true; sheetMode = '' }
    const act = $('tocList').querySelector('.toc-item.active')
    if (act) act.scrollIntoView({ block: 'nearest' })
  }
  // ===== ディクテーション（書き取り）モード =====
  // 同じ media を流用してオーバーレイを開く。区間再生はオーバーレイ側が自前の timeupdate で行う。
  // 開いている間は本体のシーク/ループ操作と競合しないよう、閉じるまで本体 UI はオーバーレイで覆う。
  let dictSession = null
  const btnDict = $('btnDict')
  if (btnDict) btnDict.onclick = () => {
    if (dictSession && dictSession.isActive()) { dictSession.close(); return }
    try { media.pause() } catch { /* noop */ }
    dictOpen = true           // 本体の rAF ループ強制(A–B/loopSeg)と ended 自動次送りを黙らせる
    dictSession = openDictation({
      root: rp,
      media,
      hash,
      mainCues,                 // offset 適用済みの現在の cue（タイミングが音声と一致）
      onClose: () => { dictSession = null; dictOpen = false },  // 閉じたら本体ループ復帰（状態は保持していたのでそのまま効く）
      // 採点結果の語タップでプレーヤ本体のタップ辞書ポップアップを流用（スクラブ辞書と同じ見た目）。
      onWordTap: (wordEl, x, y) => showDictFor(wordEl, { x, y }),
    })
  }

  const btnToc = $('btnToc')
  if (btnToc) btnToc.onclick = () => {
    if (!sheet.hidden && sheetMode === 'toc') { sheet.hidden = true; sheetMode = '' }
    else { renderTocSheet(); sheetMode = 'toc'; sheet.hidden = false }
  }

  // ===== 没入モード（全画面）: chrome を隠して動画＋字幕のみ。中央タップで再生/一時停止 =====
  // ※ iPhone(PWA) は <video> 以外の本物のOS全画面が使えないため、CSSの没入モードで実現する。
  //   状態は「縦＝ボタンあり / 横＝全画面」の2つだけ。向きに完全連動させ、中間状態（横でボタンあり）は作らない。
  const immFlashEl = $('immFlash'), immSkipEl = $('immSkip')
  let immersive = false
  const setImmersive = (on) => {
    if (immersive === on) return
    immersive = on
    rp.classList.toggle('immersive', on)
  }
  // 中央タップ＝再生/一時停止。直後に状態アイコンの残像をひと瞬き見せて消す（フィードバック）。
  const immTogglePlay = () => {
    if (media.paused) media.play(); else media.pause()
    immFlashEl.innerHTML = media.paused ? ICONS.pause : ICONS.play
    immFlashEl.classList.remove('flash'); void immFlashEl.offsetWidth; immFlashEl.classList.add('flash') // アニメ再始動
  }
  // ダブルタップ秒送り/戻しの ± 残像（没入中。全画面offと同じ円形バッジを左右に出す）。
  const immSkipFlash = (dir, sec) => {
    immSkipEl.textContent = dir < 0 ? `−${sec}` : `+${sec}`
    immSkipEl.classList.remove('left', 'right'); immSkipEl.classList.add(dir < 0 ? 'left' : 'right')
    immSkipEl.classList.remove('flash'); void immSkipEl.offsetWidth; immSkipEl.classList.add('flash') // アニメ再始動
  }
  // 横向きで自動的に没入・縦で自動解除。
  const mqLand = window.matchMedia('(orientation: landscape)')
  const onOrient = () => {
    if (!rp.isConnected) { mqLand.removeEventListener('change', onOrient); return } // 画面離脱でリスナ解除
    setImmersive(mqLand.matches)
  }
  mqLand.addEventListener('change', onOrient)
  if (mqLand.matches) setImmersive(true) // 開いた時点で既に横なら没入

  // 位置復元・状態保存
  const st = await d.get('state', hash)
  if (st && st.pos && st.pos > 1) media.addEventListener('loadedmetadata', () => { media.currentTime = st.pos }, { once: true })
  async function saveState() {
    try {
      await d.put('state', { pos: media.currentTime || 0, bookmarks: (st && st.bookmarks) || [], updatedAt: new Date().toISOString(), dirty: true }, hash)
    } catch { /* noop */ }
  }
  media.addEventListener('pause', saveState)

  requestAnimationFrame(tick)
}
